import React, { useState, useEffect } from "react";
import timezones from "timezones-list";
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Card,
  Image,
  Dropdown,
} from "react-bootstrap"; // Removed ButtonGroup
import { toast } from "react-toastify";

// Utility function to calculate timezone offsets
const calculateTimezoneOffsets = () => {
  const offsets = {};
  timezones.forEach((tz) => {
    if (tz.tzCode === "Europe/Zaporizhzhia") {
      return; // Skip problematic timezone
    }
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone: tz.tzCode,
        timeZoneName: "shortOffset",
      });
      const parts = formatter.formatToParts(now);
      const timeZonePart = parts.find((part) => part.type === "timeZoneName");

      if (timeZonePart && timeZonePart.value) {
        const offsetStr = timeZonePart.value.replace("GMT", "").trim();
        let formattedOffset;

        // Extract sign (+ or -) and the rest
        const sign = offsetStr.startsWith("-") ? "-" : "+";
        const timePart = offsetStr.replace(/^[+-]/, ""); // Remove sign

        if (timePart.includes(":")) {
          // Case: Has minutes (e.g., "5:30" or "11:45")
          const [hours, minutes] = timePart.split(":");
          formattedOffset = `${sign}${hours.padStart(2, "0")}:${minutes}`;
        } else {
          // Case: No minutes (e.g., "8" → "08:00")
          formattedOffset = `${sign}${timePart.padStart(2, "0")}:00`;
        }

        offsets[tz.tzCode] = `(GMT${formattedOffset})`;
      } else {
        offsets[tz.tzCode] = ""; // Fallback if offset can't be determined
      }
    } catch (error) {
      console.error(`Error getting offset for ${tz.tzCode}:`, error);
      offsets[tz.tzCode] = ""; // Fallback on error
    }
  });
  return offsets;
};

const timezoneOffsets = calculateTimezoneOffsets();

const BusinessHours = ({ initialData, onUpdate }) => {
  const [show, setShow] = useState(false);
  const [timezone, setTimezone] = useState(initialData?.timezonesInit);
  const [businessHours, setBusinessHours] = useState([]);
  const [tempHours, setTempHours] = useState([]);

  // Removed unused isDropdownOpen and setIsDropdownOpen

  useEffect(() => {
    setTimezone(initialData?.timezonesInit);
    if (initialData?.businessHours) {
      const updatedBusinessHours = [
        "Monday",
        "Tuesday",
        "Wednesday",
        "Thursday",
        "Friday",
        "Saturday",
        "Sunday",
      ].map((day) => {
        const existingDay = initialData.businessHours.find(
          (d) => d.day.toLowerCase() === day.toLowerCase()
        );

        let from = "";
        let to = "";
        let open = false;

        if (existingDay?.hours && !existingDay.hours.includes("undefined")) {
          if (existingDay.hours !== "Closed") {
            const [start, end] = existingDay.hours.split(" - ");
            from = start || "";
            to = end || "";
            open = true;
          }
        }

        return { day, from, to, open };
      });

      setBusinessHours(updatedBusinessHours);
      setTempHours([...updatedBusinessHours]);
    }
  }, [initialData]);

  const times = [
    "09:00AM",
    "10:00AM",
    "11:00AM",
    "12:00PM",
    "01:00PM",
    "02:00PM",
    "03:00PM",
    "04:00PM",
    "05:00PM",
    "05:30PM",
  ];

  const handleToggle = (index) => {
    const updatedHours = [...tempHours];
    updatedHours[index].open = !updatedHours[index].open;
    if (!updatedHours[index].open) {
      updatedHours[index].from = "";
      updatedHours[index].to = "";
    } else {
      updatedHours[index].from = "09:00AM";
      updatedHours[index].to = "05:00PM";
    }
    setTempHours(updatedHours);
  };

  const convertTo24HourFormat = (time) => {
    const [hour, minutePart] = time.slice(0, -2).split(":");
    const period = time.slice(-2);
    let hours = parseInt(hour, 10);
    const minutes = parseInt(minutePart || "00", 10);

    if (period === "PM" && hours !== 12) {
      hours += 12;
    } else if (period === "AM" && hours === 12) {
      hours = 0;
    }

    return hours * 60 + minutes; // Convert to total minutes for easy comparison
  };

  const handleTimeChange = (index, field, value) => {
    const updatedHours = [...tempHours];
    updatedHours[index][field] = value;

    const fromMinutes = convertTo24HourFormat(updatedHours[index].from);
    const toMinutes = convertTo24HourFormat(updatedHours[index].to);

    if (fromMinutes >= toMinutes) {
      toast.error("From time must be less than To time", {
        position: "top-right",
        autoClose: 1000,
      });
      return;
    }

    setTempHours(updatedHours);
  };

  const handleSave = () => {
    setBusinessHours(tempHours);
    setShow(false);

    // Send updated data to parent
    onUpdate({
      timezonesInit: timezone,
      bussinessHours: tempHours.map(({ day, from, to, open }) => ({
        day,
        hours: open ? `${from} - ${to}` : "", // Send empty string if closed
      })),
    });
  };

  const handleCancel = () => {
    setTempHours([...businessHours]);
    setShow(false);
  };

  return (
    <Row className="px-4 body-font">
      <Col>
        <Card className="business_details_border">
          <Card.Header className="px-4 body-font fw-bold fs-two d-flex justify-content-between business_info_card_header">
            <div className="d-flex black-color" style={{ gap: "12px" }}>
              <Image src="/assets/clock-colored.svg" alt="Business Hours" />
              Business Hours
            </div>
            <div
              className="d-flex gap-1 align-items-center cursor-pointer"
              onClick={() => setShow(true)}
            >
              <Image
                src="/assets/edit-1.svg"
                alt="Edit"
                className="business-info-icon"
              />
              <span className="fs-four" style={{ color: "#5925DC" }}>
                Edit
              </span>
            </div>
          </Card.Header>
          <Card.Body className="p-4 d-flex flex-column fs-four black-color">
            <div className="d-flex flex-column flex-md-row justify-content-between business-hrs-item pb-4">
              <span className="fw-medium">Timezone:</span>
              <span className="fw-normal">{timezone}</span>
            </div>
            <div className="business-hrs-border-bottom mb-4"></div>
            <div className="d-flex flex-column" style={{ gap: "12px" }}>
              {businessHours.map((day, index) => (
                <div
                  key={index}
                  className="d-flex flex-column flex-md-row justify-content-between business-hrs-item"
                >
                  <span className="fw-medium">{day.day}</span>
                  <span className="fw-normal">
                    {day.open ? `${day.from} - ${day.to}` : "Closed"}
                  </span>
                </div>
              ))}
            </div>
          </Card.Body>
        </Card>
      </Col>

      <Modal show={show} onHide={handleCancel} centered size="lg">
        <Modal.Header
          closeButton
          className="business-hrs-border-bottom"
          style={{ backgroundColor: "#FAFAFA" }}
        >
          <Modal.Title className="fw-medium black-color fs-five">
            Business Hours
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="direction-modal-body">
          <Form.Group className="mb-4 business-hrs-timezone">
            <Form.Label
              className="fs-four fw-medium"
              style={{ color: "#414651" }}
            >
              Timezone
            </Form.Label>
            <Dropdown>
              <Dropdown.Toggle
                variant="outline-primary"
                className="w-100 text-start business-hrs-dropdown black-color justify-content-between fs-6 d-flex align-items-center"
              >
                {timezone}
                <Image
                  src="/assets/chevron-down.svg"
                  alt="Arrow Down"
                  className="ms-auto" // Align arrow to the right
                />
              </Dropdown.Toggle>
              <Dropdown.Menu style={{ maxHeight: "300px", overflowY: "auto" }}>
                {timezones.map((tz, idx) => (
                  <Dropdown.Item
                    key={idx}
                    onClick={() => setTimezone(tz.tzCode)}
                    active={timezone === tz.tzCode}
                    style={{
                      backgroundColor:
                        timezone === tz.tzCode ? "#5925dc" : "transparent",
                      color: timezone === tz.tzCode ? "white" : "#181D27",
                    }}
                  >
                    {timezoneOffsets[tz.tzCode] || ""} {tz.tzCode}
                  </Dropdown.Item>
                ))}
              </Dropdown.Menu>
            </Dropdown>
          </Form.Group>
          <div
            className="mb-4"
            style={{ borderBottom: "1px solid #d5d7da" }}
          ></div>

          {tempHours.map((day, index) => (
            <div
              key={index}
              className="mb-4 gap-2 gap-lg-0 align-items-center d-flex flex-column flex-lg-row"
            >
              <div className="d-flex align-items-center business-hrs-toggle">
                <Form.Check
                  type="switch"
                  id={`switch-${index}`}
                  checked={day.open}
                  onChange={() => handleToggle(index)}
                  className="custom-switch"
                />
                <span className="fs-6 fw-medium black-color">{day.day}</span>
              </div>
              {day.open ? (
                <div className="ps-lg-5 ps-0 d-flex gap-4 justify-content-between flex-lg-row flex-column w-lg-70 w-100">
                  <div className="d-flex align-items-center border rounded pe-0 business-hrs-time-dropdown">
                    <span
                      className="fs-6 business-hrs-timeselection"
                      style={{ maxWidth: "51px" }}
                    >
                      From
                    </span>
                    <span className="vr" />
                    <Dropdown className="w-100">
                      <Dropdown.Toggle
                        variant="outline-primary"
                        className="black-color w-100 text-start border-0 business-hrs-dropdown fs-6 d-flex align-items-center"
                      >
                        {day.from}
                        <Image
                          src="/assets/chevron-down.svg"
                          alt="Arrow Down"
                          className="ms-auto" // Align arrow to the right
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {times.map((time, idx) => (
                          <Dropdown.Item
                            key={idx}
                            onClick={() =>
                              handleTimeChange(index, "from", time)
                            }
                            active={day.from === time}
                            style={{
                              backgroundColor:
                                day.from === time ? "#5925dc" : "transparent",
                              color: day.from === time ? "white" : "#181D27",
                            }}
                          >
                            {time}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                  <div className="d-flex align-items-center border rounded pe-0  business-hrs-time-dropdown">
                    <span
                      className="fs-6 business-hrs-timeselection"
                      style={{ maxWidth: "51px" }}
                    >
                      To
                    </span>
                    <span className="vr" />
                    <Dropdown className="w-100">
                      <Dropdown.Toggle
                        variant="outline-primary"
                        className="black-color w-100 text-start border-0 business-hrs-dropdown fs-6 d-flex align-items-center"
                      >
                        {day.to}
                        <Image
                          src="/assets/chevron-down.svg"
                          alt="Arrow Down"
                          className="ms-auto" // Align arrow to the right
                        />
                      </Dropdown.Toggle>
                      <Dropdown.Menu>
                        {times.map((time, idx) => (
                          <Dropdown.Item
                            key={idx}
                            onClick={() => handleTimeChange(index, "to", time)}
                            active={day.to === time}
                            style={{
                              backgroundColor:
                                day.to === time ? "#5925dc" : "transparent",
                              color: day.to === time ? "white" : "#181D27",
                            }}
                          >
                            {time}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </div>
                </div>
              ) : (
                <div className="d-flex fs-6 fw-normal gap-2 border rounded w-100 fs-6 business-hrs-closed">
                  <Image src="/assets/moon.svg" /> Closed
                </div>
              )}
            </div>
          ))}
        </Modal.Body>
        <Modal.Footer className="gap-4 business-info-modal-footer">
          <span className="gray-color fs-four m-0">
            Changes will go live immediately
          </span>
          <div className="gap-3 m-0 d-flex">
            <Button
              className="m-0 direction-modal-button fw-bold fs-four border-0 direction-modal-cancel"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              className="direction-modal-button m-0 btn-primary-style fw-bold px-3 fs-four"
              style={{ color: "#ffffff" }}
              onClick={handleSave}
            >
              Save
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </Row>
  );
};

export { timezones, timezoneOffsets };

export default BusinessHours;
